/**
 * RealPeerManager - Production implementation of IPeerManager
 *
 * Wraps the legacy peer infrastructure (@playcastdotio/Peer) for WebRTC operations.
 * This is a thin adapter layer that allows coordinators to manage peers
 * without directly importing the legacy peer code.
 */

import type { IPeerManager } from './IPeerManager';
import type { PlaycastPeerHost, PlaycastHostPeerStatus } from '@playcastdotio/Peer';
import { createHost } from '@playcastdotio/Peer';
import { getStateStore } from '../../api/stores';
import { sendMessage } from '../../api/modules/realtime';

// Import peer-specific handlers (these will stay in the legacy code for now)
import {
  initializePlayer,
  cleanupPeerStatsCollection,
  validatorFunction,
  setHostPeer,
} from '../../api/modules/peers/host';
import { cleanupActivityListeners } from '../../api/modules/managers/playerActivity';

const state = getStateStore('ipc');
let hostPeer: PlaycastPeerHost | null = null;

export class RealPeerManager implements IPeerManager {
  private statusCallbacks: Array<(status: PlaycastHostPeerStatus) => void> = [];
  private remotePeerCallbacks: Array<(event: { added: string[]; removed: string[] }) => void> = [];

  setupHostPeer(hostPeerId: string): void {
    console.log('[RealPeerManager] Setting up host peer:', hostPeerId);

    // Close existing peer if present
    if (hostPeer) {
      console.log('[RealPeerManager] Closing existing peer before creating new one');
      hostPeer.close('host:duplicate_peer');
      hostPeer = null;
    }

    // Get ICE servers from state
    const iceServers = state.iceServerState.currentState.hostIceServers;

    // Create the host peer
    hostPeer = createHost(
      {
        validatorFunction,
        realtimeApiManager: {
          send: (message) => {
            sendMessage(message);
          },
        },
        iceServers: iceServers || undefined,
        forceTurn: !state.iceServerState.currentState.allowPeerConnections,
      },
      hostPeerId,
    );

    // Set up quality preset listener
    hostPeer.qualityPresetStore.addListener((presetGroup) => {
      if (presetGroup) {
        state.webrtcPresetGroups.updateState(presetGroup);
      }
    });
    state.webrtcPresetGroups.updateState(hostPeer.qualityPresetStore.currentState);

    // Set up status listener
    let cachedHostPeerStatus = hostPeer.status.currentState;
    const handleStatusChange = async (status: PlaycastHostPeerStatus) => {
      console.debug('[RealPeerManager] Peer status changed:', status);
      cachedHostPeerStatus = status;

      // Notify all subscribers
      this.statusCallbacks.forEach((callback) => {
        try {
          callback(status);
        } catch (error) {
          console.error('[RealPeerManager] Status callback error:', error);
        }
      });
    };

    hostPeer.status.addListener((status) => {
      if (cachedHostPeerStatus === status) return;
      handleStatusChange(status);
    });

    // Trigger initial status callback
    handleStatusChange(hostPeer.status.currentState);

    // STRANGLER-FIG: Bridge to legacy system
    // Legacy peer message handlers (hostHandlePeerMessage) need access to the peer
    // TODO: Remove this when legacy handlers are migrated to coordinators
    setHostPeer(hostPeer);

    // Set up remote peers listener
    let cachedPeerIds = Object.keys(hostPeer.remotePeers.currentState);
    hostPeer.remotePeers.addListener((peers) => {
      const addedPeers = Object.keys(peers).filter((peer) => !cachedPeerIds.includes(peer));
      const removedPeers = cachedPeerIds.filter((peer) => !Object.keys(peers).includes(peer));
      cachedPeerIds = Object.keys(peers);

      // Notify subscribers
      if (addedPeers.length > 0 || removedPeers.length > 0) {
        this.remotePeerCallbacks.forEach((callback) => {
          try {
            callback({ added: addedPeers, removed: removedPeers });
          } catch (error) {
            console.error('[RealPeerManager] Remote peer callback error:', error);
          }
        });
      }

      // Handle added peers (initialize player)
      addedPeers.forEach((peerId) => {
        console.debug('[RealPeerManager] Adding peer:', peerId);
        const peer = peers[peerId];
        if (peer && 'status' in peer && peer.status.currentState.connectionState === 'connected') {
          initializePlayer(peer as any);
        }
      });

      // Handle removed peers (cleanup)
      removedPeers.forEach((peerId) => {
        console.debug('[RealPeerManager] Removing peer:', peerId);

        // Clean up stats collection
        cleanupPeerStatsCollection(peerId);

        // Clean up player state
        const playerState = structuredClone(state.playerState.currentState.players);
        delete playerState[peerId];
        state.playerState.updateState({ players: playerState });

        // Clean up activity listeners
        cleanupActivityListeners(peerId);
        const playerActivityState = structuredClone(state.playerActivityState.currentState.players);
        delete playerActivityState[peerId];
        state.playerActivityState.updateState({
          inactive: state.playerActivityState.currentState.inactive,
          players: { ...playerActivityState },
        });

        // Clean up player stats
        const playerStatState = structuredClone(state.playerStatState.currentState.players);
        delete playerStatState[peerId];
        state.playerStatState.updateState({ players: { ...playerStatState } });

        // Remove peer from WebRTC stats
        const currentStats = state.hostWebrtcStatsState.currentState;
        const newStats = { ...currentStats };
        delete newStats[peerId];
        state.hostWebrtcStatsState.updateState(newStats);
      });
    });

    console.log('[RealPeerManager] ✅ Host peer setup complete');
  }

  closeHostPeer(reason?: string): void {
    console.log('[RealPeerManager] Closing host peer:', reason);

    if (hostPeer) {
      hostPeer.close(reason || 'host:coordinator_requested');
      hostPeer = null;

      // STRANGLER-FIG: Clear legacy peer reference
      setHostPeer(null);
    }
  }

  getHostPeer(): PlaycastPeerHost | null {
    return hostPeer;
  }

  onStatusChange(callback: (status: PlaycastHostPeerStatus) => void): () => void {
    this.statusCallbacks.push(callback);
    return () => {
      const index = this.statusCallbacks.indexOf(callback);
      if (index > -1) {
        this.statusCallbacks.splice(index, 1);
      }
    };
  }

  onRemotePeerChange(callback: (event: { added: string[]; removed: string[] }) => void): () => void {
    this.remotePeerCallbacks.push(callback);
    return () => {
      const index = this.remotePeerCallbacks.indexOf(callback);
      if (index > -1) {
        this.remotePeerCallbacks.splice(index, 1);
      }
    };
  }
}
